import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../lib/api';
import { Button, ErrorBanner, Field, Spinner, inputCls } from '../components/ui';
import { Icon } from '../components/Icon';
import { EnvironmentBadge, environmentLabel } from '../components/EnvironmentBadge';

export function LoginPage() {
  const { status, login, homePath, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authed' && user) return <Navigate to={homePath} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await login(username.trim(), password);
      navigate(homePath, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not sign in. Check your internet connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-50 text-slate-900">
        <Spinner className="h-8 w-8 text-accent" />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center p-4 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-accent-ink shadow-lg shadow-accent/30">
            <Icon name="washing" className="h-8 w-8" />
          </span>
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">Cannery Laundry Hub</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to your account</p>
          <EnvironmentBadge className="mt-3" />
          {environmentLabel() && (
            <p className="mt-2 text-xs font-medium text-amber-700">
              {environmentLabel()} environment — test data only. Production is unaffected.
            </p>
          )}
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_1px_3px_0_rgba(0,0,0,0.08),0_1px_2px_-1px_rgba(0,0,0,0.04)]"
          aria-label="Sign in"
        >
          <div className="space-y-4">
            {error && <ErrorBanner message={error} />}
            <Field label="Username" htmlFor="username">
              <input
                id="username"
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={inputCls}
                placeholder="e.g. admin"
              />
            </Field>
            <Field label="Password" htmlFor="password">
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="Your password"
              />
            </Field>
            <Button type="submit" size="lg" className="w-full" disabled={busy}>
              {busy ? (
                <>
                  <Spinner className="h-5 w-5" />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </div>
          <p className="mt-5 text-center text-xs text-slate-400">
            An internet connection is required. Contact your administrator if you forgot your
            password.
          </p>
        </form>
      </div>
    </div>
  );
}